import { authStore } from '@/lib/authStore';
import { attemptTokenRefresh } from '@/lib/tokenRefresh';

const AUTH_ERROR_STATUSES = new Set([401, 419, 440]);

// Explicit session timeout statuses — never attempt refresh for these
const EXPLICIT_TIMEOUT_STATUSES = new Set([419, 440]);

// Retry-once guard: prevents infinite refresh loops
let refreshAttempted = false;

const dispatchExpiredEvent = (status: number) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent('auth:expired', {
      detail: { reason: 'server_rejection', status },
    }),
  );
};

export const handleAuthFailure = (status: number): void => {
  if (!AUTH_ERROR_STATUSES.has(status)) {
    return;
  }
  try {
    authStore.logout();
  } catch {
    // ignore
  }
  dispatchExpiredEvent(status);
};

export const shouldTreatAsAuthFailure = (status: number): boolean => {
  return AUTH_ERROR_STATUSES.has(status);
};

/**
 * Attempt a token refresh before logging out.
 *
 * Only for 401 — never for 419/440 (explicit session timeouts) or 403 (RBAC).
 * Returns `true` if refresh succeeded (caller should retry the request).
 * Returns `false` if refresh failed (caller should proceed with logout).
 * Retry-once guard prevents infinite loops.
 */
export async function tryRefreshBeforeLogout(status: number): Promise<boolean> {
  // Never refresh for explicit timeouts or non-401 statuses
  if (status !== 401 || EXPLICIT_TIMEOUT_STATUSES.has(status)) {
    return false;
  }

  // Retry-once: don't attempt refresh again if we already tried
  if (refreshAttempted) {
    return false;
  }

  refreshAttempted = true;
  try {
    const success = await attemptTokenRefresh();
    if (!success) {
      refreshAttempted = false;
      return false;
    }
    // Reset the guard so future 401s can trigger one more refresh
    refreshAttempted = false;
    return true;
  } catch {
    refreshAttempted = false;
    return false;
  }
}

/** Exposed for testing only. */
export function _resetRefreshGuard(): void {
  refreshAttempted = false;
}
